import { useState, type FormEvent } from "react";
import type { AccountOut, AccountType } from "@shop-ledger/shared-types";
import { Button, DataTable, Input, type DataTableColumn, useSortableData, useTableFilter } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createAccount, listAccounts } from "../../api/ledgerApi";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

const ACCOUNT_TYPES: AccountType[] = ["ASSET", "LIABILITY", "EQUITY", "REVENUE", "EXPENSE"];

export function ChartOfAccountsPage() {
  const { ensureFreshToken } = useAuth();
  const accountsState = useAsyncData(() => ensureFreshToken().then((token) => listAccounts(token)), []);

  const { filteredData, query, setQuery } = useTableFilter(
    accountsState.data ?? [],
    (account) => `${account.code} ${account.name} ${account.type}`,
  );
  const { sortedData, sort, requestSort } = useSortableData<AccountOut, keyof AccountOut>(filteredData, (row, key) =>
    String(row[key]),
  );

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState<AccountType>("ASSET");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createAccount(token, { code, name, type });
      setCode("");
      setName("");
      setType("ASSET");
      accountsState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not create account.");
    } finally {
      setSubmitting(false);
    }
  }

  const columns: DataTableColumn<AccountOut, keyof AccountOut>[] = [
    { key: "code", header: "Code", sortable: true, render: (a) => a.code },
    { key: "name", header: "Name", sortable: true, render: (a) => a.name },
    { key: "type", header: "Type", sortable: true, render: (a) => a.type },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Chart of Accounts</h1>
          <p className="page__subtitle">Every GL account for this tenant.</p>
        </div>
      </div>

      {accountsState.error ? <StatusBanner kind="error">{accountsState.error}</StatusBanner> : null}

      <div className="panel">
        <h2 className="panel__title">New account</h2>
        <form onSubmit={handleCreate}>
          <div className="form-grid">
            <Input label="Code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. 6000" required />
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Rent Expense"
              required
            />
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="account-type">
                Type
              </label>
              <select
                id="account-type"
                className="sl-input"
                value={type}
                onChange={(e) => setType(e.target.value as AccountType)}
              >
                {ACCOUNT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
          <div className="form-actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create account"}
            </Button>
          </div>
        </form>
      </div>

      <div className="panel">
        <div className="filter-bar">
          <div className="sl-field">
            <label className="sl-field__label" htmlFor="account-search">
              Search
            </label>
            <input
              id="account-search"
              className="sl-input"
              placeholder="code, name, or type"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        {accountsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={sortedData}
            getRowId={(row) => row.id}
            sort={sort}
            onSortChange={requestSort}
            emptyMessage="No accounts yet."
            caption="Chart of Accounts"
          />
        )}
      </div>
    </div>
  );
}
